import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { localTimeOfPunch } from './punchPlan';
import { weekdayName } from '../rangeUtils';
import { DayDataResponse, DayEntry, PunchItem, ScheduleEvent, ScheduleShift } from '../types';

interface DayDataTableProps {
  dayData: DayDataResponse;
  onAddPunch: (date: string) => void;
  onDeclinePunch: (uuid: string) => void;
  disabled?: boolean;
  /** When true, this environment cannot create or decline punches: the "+ punch" link and the punch chips' delete icon are not rendered at all. `disabled` keeps its own meaning (data is stale). */
  readOnly?: boolean;
}

/** One chip per schedule event, except a flexible/duration break pair (time: null) which renders as a single chip on break_start; the matching break_end is dropped. */
const visibleEvents = (events: ScheduleEvent[]): ScheduleEvent[] => events.filter((event) => !(event.type === 'break_end' && event.time === null));

const eventLabel = (event: ScheduleEvent): string => {
  if (event.time === null) {
    const mode = event.breakMode ?? 'duration';
    return `— break ${event.duration ?? 0} min, ${mode}`;
  }
  const hh = Math.floor((event.time % 1440) / 60)
    .toString()
    .padStart(2, '0');
  const mm = (event.time % 60).toString().padStart(2, '0');
  return `${hh}:${mm} ${event.type}`;
};

const punchLabel = (punch: PunchItem, timeZone: string): string => `${localTimeOfPunch(punch.deviceDatetime, timeZone)} ${punch.punchType}`;

const summaryCounts = (dayData: DayDataResponse) => {
  const shifts = dayData.days.reduce((sum, d) => sum + d.shifts.length, 0);
  const punches = dayData.days.reduce((sum, d) => sum + d.punches.length, 0);
  const pending = dayData.days.reduce((sum, d) => sum + d.punches.filter((p) => p.status === 'pending').length, 0);
  return { days: dayData.days.length, shifts, punches, pending };
};

const ScheduleCell = ({ shift }: { shift: ScheduleShift | null }) => {
  const isNonWorking = shift && (!shift.working || shift.nonWorkingDayShift);
  return (
    <TableCell>
      {!shift ? (
        <Typography variant="body2" color="text.secondary">
          no shift
        </Typography>
      ) : isNonWorking ? (
        <Chip size="small" label={shift.isDsrDay ? 'DSR' : 'non-working'} sx={{ bgcolor: 'action.selected' }} />
      ) : (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {visibleEvents(shift.events).map((event) => (
            <Chip key={event.key} size="small" label={eventLabel(event)} color="primary" variant="outlined" />
          ))}
        </Box>
      )}
    </TableCell>
  );
};

/** One row per `(day, shift)`. The Day, Punches and Actions cells belong to the DAY, not the shift — they render (with `rowSpan`) only on the day's first row, so a multi-shift day does not repeat its punch chips once per shift. */
const ShiftRow = ({
  day,
  shift,
  timeZone,
  isFirst,
  rowSpan,
  disabled,
  readOnly,
  onAddPunch,
  onDeclinePunch
}: {
  day: DayEntry;
  shift: ScheduleShift | null;
  timeZone: string;
  isFirst: boolean;
  rowSpan: number;
  disabled: boolean;
  readOnly: boolean;
  onAddPunch: (date: string) => void;
  onDeclinePunch: (uuid: string) => void;
}) => {
  const isNonWorking = shift && (!shift.working || shift.nonWorkingDayShift);
  return (
    <TableRow>
      {isFirst && (
        <TableCell rowSpan={rowSpan}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {day.date}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {weekdayName(day.date)}
          </Typography>
        </TableCell>
      )}
      <ScheduleCell shift={shift} />
      {isFirst && (
        <TableCell rowSpan={rowSpan}>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {day.punches.map((punch) =>
              readOnly ? (
                <Chip key={punch.uuid} size="small" disabled={disabled} label={punchLabel(punch, timeZone)} color={punch.status === 'pending' ? 'warning' : 'success'} />
              ) : (
                <Chip key={punch.uuid} size="small" disabled={disabled} label={punchLabel(punch, timeZone)} color={punch.status === 'pending' ? 'warning' : 'success'} onDelete={disabled ? undefined : () => onDeclinePunch(punch.uuid)} deleteIcon={<CloseIcon />} />
              )
            )}
            {day.punches.length === 0 && shift && !isNonWorking && (
              <Typography variant="body2" color="warning.main">
                no punches
              </Typography>
            )}
            {day.ignoredCount > 0 && (
              <Typography variant="body2" color="text.secondary">
                {day.ignoredCount} declined/ignored/invalid
              </Typography>
            )}
          </Box>
        </TableCell>
      )}
      {isFirst && (
        <TableCell rowSpan={rowSpan}>
          {!readOnly && (
            <Link component="button" variant="body2" disabled={disabled} onClick={() => onAddPunch(day.date)}>
              + punch
            </Link>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

export const DayDataTable = ({ dayData, onAddPunch, onDeclinePunch, disabled = false, readOnly = false }: DayDataTableProps) => {
  const counts = summaryCounts(dayData);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Time zone: {dayData.context.timeZone} ({dayData.context.timeZoneSource}) · {counts.days} days · {counts.shifts} shifts · {counts.punches} punches · {counts.pending} pending
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Day</TableCell>
            <TableCell>Schedule</TableCell>
            <TableCell>Punches</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {dayData.days.map((day) => {
            const rowSpan = Math.max(day.shifts.length, 1);
            return day.shifts.length === 0 ? (
              <ShiftRow key={day.date} day={day} shift={null} timeZone={dayData.context.timeZone} isFirst rowSpan={rowSpan} disabled={disabled} readOnly={readOnly} onAddPunch={onAddPunch} onDeclinePunch={onDeclinePunch} />
            ) : (
              day.shifts.map((shift, index) => (
                <ShiftRow key={`${day.date}-${shift.shiftId}`} day={day} shift={shift} timeZone={dayData.context.timeZone} isFirst={index === 0} rowSpan={rowSpan} disabled={disabled} readOnly={readOnly} onAddPunch={onAddPunch} onDeclinePunch={onDeclinePunch} />
              ))
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default DayDataTable;
